/**
 * How a thing has been going, said honestly about the window it covers.
 *
 * Mirrors `keeping_up_words` in `src/domains/occurrences/read_model.py` — the
 * backend sends the same sentence as `card.words` for the list previews, and
 * these surfaces compose their own line from the same numbers. One helper so
 * the three of them (goal, dream, skill) cannot drift apart again.
 *
 * Owner, 2026-09-08: *"It should only display anything since the time the goal
 * or a dream or a habit or a skill or a task were created and showing it for
 * the last 30 days is futile."* A goal created that morning read "1 day with
 * something done in the last 30" beside thirteen empty cells for days it had
 * not been alive.
 *
 * Three shapes, because one sentence cannot serve all three ages:
 *   added today          nothing to report yet, and no history to imply
 *   2 of 5 days …        younger than the window: name the window it has
 *   4 days … in the last 30   it has lived the whole window
 */

export interface KeepingUpWindow {
  /** Days with something done, counted only since the item existed. */
  days_active: number
  /** How many days the item has actually been around, capped at 30. */
  window_days: number
  /** True when the item is old enough for the full window to apply. */
  window_capped: boolean
  /** Days with something done over all time, however far back. */
  days_total?: number
}

export function keepingUpWords(
  card: KeepingUpWindow,
  { noun = 'day', done = 'with something done', born = 'added today', quiet, none }:
    { noun?: string; done?: string; born?: string; quiet: string; none: string },
): string {
  const { days_active: count, window_days: span, window_capped: capped } = card
  if (span <= 1) return born
  if (!count) return card.days_total ? quiet : none
  const head = capped
    ? `${count} ${noun}${count === 1 ? '' : 's'}`
    : `${count} of ${span} ${noun}s`
  const tail = capped ? `in the last ${span}` : ''
  return [head, done, tail].filter(Boolean).join(' ')
}
